import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body?: string | null;
  readAt?: string | null;
  createdAt: string;
  event?: { id: string; summary: string; startsAt: string } | null;
}

export function NotificationsPage() {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [pushState, setPushState] = useState<'unsupported' | 'default' | 'granted' | 'denied'>('default');
  const toast = useToast();

  const load = async () => {
    const res = await api.get<{ items: NotificationItem[]; unread: number }>('/api/notifications');
    setItems(res.items);
    setUnread(res.unread);
  };

  useEffect(() => {
    load();
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushState('unsupported');
    } else {
      setPushState(Notification.permission);
    }
  }, []);

  const markRead = async () => {
    await api.post('/api/notifications/read');
    toast.success('Benachrichtigungen gelesen');
    load();
  };

  const enablePush = async () => {
    if (pushState === 'unsupported') return;
    const permission = await Notification.requestPermission();
    setPushState(permission);
    if (permission !== 'granted') return toast.error('Push wurde nicht erlaubt');
    const registration = await navigator.serviceWorker.register('/sw.js');
    const { publicKey } = await api.get<{ publicKey: string }>('/api/push/public-key');
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    await api.post('/api/push/subscriptions', subscription.toJSON());
    toast.success('Push-Benachrichtigungen aktiviert');
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h3">Benachrichtigungen</h1>
          <div className="text-small text-ink-muted">{unread} ungelesen</div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={enablePush} disabled={pushState === 'unsupported' || pushState === 'denied' || pushState === 'granted'}>
            {pushState === 'granted' ? 'Push aktiv' : pushState === 'denied' ? 'Push blockiert' : 'Push aktivieren'}
          </button>
          <button className="btn btn-secondary" onClick={markRead} disabled={unread === 0}>Alle gelesen</button>
        </div>
      </div>
      <section className="card !p-0 overflow-hidden">
        {items.map((item) => (
          <div key={item.id} className="border-b border-border/70 p-4 flex gap-3 items-start">
            <Badge tone={item.readAt ? 'muted' : tone(item.type)}>{label(item.type)}</Badge>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{item.title}</div>
              {item.body && <div className="text-small text-ink-secondary mt-1">{item.body}</div>}
              {item.event && <Link className="text-small text-brand-primary hover:underline" to={`/events/${item.event.id}`}>{item.event.summary}</Link>}
            </div>
            <div className="text-small text-ink-muted">{new Date(item.createdAt).toLocaleString('de-DE')}</div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-ink-muted py-8 text-small">Keine Benachrichtigungen.</div>}
      </section>
    </div>
  );
}

function tone(type: string): 'success' | 'danger' | 'warning' | 'info' | 'muted' {
  if (type === 'RSVP_ACCEPTED') return 'success';
  if (type === 'RSVP_DECLINED' || type === 'EVENT_CANCELLED') return 'danger';
  if (type === 'RSVP_TENTATIVE') return 'warning';
  return 'info';
}

function label(type: string) {
  return type.toLowerCase().replaceAll('_', ' ');
}

function urlBase64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = `${base64}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}
